import { type CSSProperties, createElement, type ReactNode } from 'react';

import classNames from 'classnames';
import classnames from 'classnames';

import { AdditionalFields } from '@/shared/additionalFields';

import GDPRCompliance from '../../signupElement/components/GDPRCompliance';

import type { Preference, Price, StripeCardElementComponent, StripePaymentElementComponent, Tier } from '../../types';
import { DEFAULT_PAYMENT_ELEMENT_OPTIONS } from '../Pricing.constants';
import { input, inputSubmit } from '../Pricing.css';

interface Props {
  email?: string;
  onEmailChange: (value: string) => void;
  taxId: string;
  onTaxIdChange: (value: string) => void;
  ctaText: string;
  isCtaDisabled: boolean;
  showTaxIdField: boolean;
  StripeCardElement?: StripeCardElementComponent;
  StripePaymentElement?: StripePaymentElementComponent;
  selectedTier?: Tier;
  selectedPrice?: Price;
  PaymentRequestButtonElement?: ReactNode;
  hasTermsOfService?: boolean;
  hasCustomTermsOfService?: string;
  inputTextColor?: CSSProperties['color'];
  inputPlaceholderColor?: CSSProperties['color'];
  inputBackgroundColor?: CSSProperties['backgroundColor'];
  additionalFieldsData?: Preference[];
  isAdditionalFieldsLabelVisible?: boolean;
  additionalFieldsStyle?: CSSProperties;
}

const InternalCheckout = ({
  email,
  onEmailChange,
  taxId,
  onTaxIdChange,
  isCtaDisabled,
  StripeCardElement,
  StripePaymentElement,
  selectedTier,
  selectedPrice,
  ctaText = 'Upgrade',
  showTaxIdField = false,
  PaymentRequestButtonElement,
  hasTermsOfService = false,
  hasCustomTermsOfService,
  inputTextColor,
  inputPlaceholderColor,
  inputBackgroundColor,
  additionalFieldsData,
  isAdditionalFieldsLabelVisible = false,
  additionalFieldsStyle = {},
}: Props) => {
  if (!selectedTier || !selectedPrice) {
    return null;
  }

  const creditCardPlaceholder = (
    <input type="text" placeholder="Credit Card Placeholder" className={classNames('py-2 px-4', input)} />
  );

  const submitButton = (
    <input
      disabled={isCtaDisabled}
      type="submit"
      value={ctaText}
      className={classNames(inputSubmit, 'rounded-md py-2 px-4')}
    />
  );

  const emailField = (
    <input
      name="email"
      defaultValue={email}
      onBlur={(e) => e.currentTarget.value && onEmailChange(e.currentTarget.value)}
      type="email"
      placeholder="Email"
      className={classnames(input, 'py-2 px-4')}
    />
  );

  const gdprField = hasTermsOfService && (
    <GDPRCompliance hasCustomTermsOfService={hasCustomTermsOfService} defaultTOSConsentValue={true} />
  );

  const taxIdField = showTaxIdField && (
    <input
      type="text"
      name="tax_id"
      placeholder="Tax ID"
      defaultValue={taxId}
      onBlur={(e) => onTaxIdChange(e.target.value)}
      className={classnames('py-2 px-4', input)}
    />
  );

  const additionalFields =
    additionalFieldsData && additionalFieldsData.length > 0 ? (
      <AdditionalFields
        data={additionalFieldsData}
        style={additionalFieldsStyle ?? {}}
        isLabelVisible={isAdditionalFieldsLabelVisible}
      />
    ) : null;

  if (PaymentRequestButtonElement) {
    return (
      <div className="flex flex-col gap-2 max-w-md w-full">
        {emailField}
        {additionalFields}
        {PaymentRequestButtonElement}
      </div>
    );
  }

  if (selectedTier?.id === 'free') {
    return (
      <div className="flex flex-col gap-2 max-w-md w-full">
        {emailField}
        {additionalFields}
        {gdprField}
        <input
          disabled={isCtaDisabled}
          type="submit"
          value="Continue as free reader"
          className={classNames(inputSubmit, 'rounded-md py-2 px-4')}
        />
      </div>
    );
  }

  if (selectedPrice?.premium_offer && !selectedPrice?.premium_offer.card_required) {
    return (
      <div className="flex flex-col gap-2 max-w-md w-full">
        {emailField}
        {additionalFields}
        {gdprField}
        <input
          disabled={isCtaDisabled}
          type="submit"
          value={ctaText}
          className={classNames(inputSubmit, 'rounded-md py-2 px-4')}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 max-w-md w-full">
      {emailField}
      {additionalFields}

      {StripePaymentElement
        ? createElement(StripePaymentElement, {
            options: { ...DEFAULT_PAYMENT_ELEMENT_OPTIONS },
          })
        : null}
      {StripeCardElement
        ? createElement(StripeCardElement, {
            className: classNames('border py-3 px-4 rounded-md', input),
            options: {
              style: {
                base: {
                  backgroundColor: inputBackgroundColor,
                  fontSize: '14px',
                  color: inputTextColor,
                  '::placeholder': {
                    color: inputPlaceholderColor,
                  },
                },
              },
            },
          })
        : null}
      {!StripePaymentElement && !StripeCardElement ? creditCardPlaceholder : null}
      {taxIdField}
      {submitButton}
    </div>
  );
};

export default InternalCheckout;
